<template>
  <main class="app-shell">
    <nav class="top-nav">
      <a class="brand" href="#top" :aria-label="t('nav.home')">
        <img class="brand-seal" src="/images/hs-logo-front-v4.png" alt="HS" width="30" height="30">
        <span class="brand-copy"><b>{{ t('brand.name') }}</b><small>HS</small></span>
      </a>
      <div class="nav-actions">
        <button class="lang-button" type="button" :aria-label="t('nav.lang')" :title="t('nav.lang')" @click="menuOpen = false; walletMenuOpen = false; langMenuOpen = !langMenuOpen"><Languages/></button>
        <button class="wallet-chip" :class="{ connected: walletAddress }" @click="handleWalletChip"><i></i>{{ walletLabel }}</button>
        <button class="menu-button" :aria-label="t('nav.menu')" @click="walletMenuOpen = false; langMenuOpen = false; menuOpen = !menuOpen"><X v-if="menuOpen"/><Menu v-else/></button>
      </div>
      <transition name="drop">
        <div v-if="langMenuOpen" class="lang-menu">
          <button type="button" :class="{ active: locale === 'zh' }" @click="switchLocale('zh')">{{ t('lang.zh') }}</button>
          <button type="button" :class="{ active: locale === 'en' }" @click="switchLocale('en')">{{ t('lang.en') }}</button>
        </div>
      </transition>
      <transition name="drop">
        <div v-if="walletMenuOpen && walletAddress" class="wallet-popover">
          <div class="wallet-popover-head"><span class="wallet-icon"><Wallet/></span><div><small>{{ t('wallet.current') }}</small><strong>{{ shortAddress(walletAddress, 9) }}</strong></div><i></i></div>
          <div class="wallet-balance-row"><span>{{ t('wallet.balance') }}</span><strong>{{ walletBalance }} BNB</strong></div>
          <div class="wallet-popover-actions"><button @click="copyText(walletAddress, t('wallet.address'))"><Copy/>{{ t('wallet.copy') }}</button><button class="disconnect-button" @click="disconnectWallet"><LogOut/>{{ t('wallet.disconnect') }}</button></div>
        </div>
      </transition>
      <transition name="drop"><div v-if="menuOpen" class="nav-menu"><a href="#buy" @click="menuOpen = false">{{ t('nav.buy') }}</a><a href="#dashboard" @click="menuOpen = false">{{ t('nav.dashboard') }}</a><a href="#share" @click="menuOpen = false">{{ t('share.title') }}</a><a href="#team" @click="menuOpen = false">{{ t('nav.team') }}</a><a href="#trust" @click="menuOpen = false">{{ t('nav.trust') }}</a></div></transition>
    </nav>

    <header id="top" class="hero" :class="{ 'hero-paused': heroPaused }">
      <HeroLandscape :paused="heroPaused"/>
      <div class="hero-content">
        <div class="hero-label">HS · GENESIS PRESALE</div>
        <h1>{{ t('hero.title') }}<span>{{ t('hero.subtitle') }}</span></h1>
        <p>{{ t('hero.tagline') }}</p>
        <div class="asset-stage">
          <button class="hero-motion" type="button" :aria-label="t(heroPaused ? 'hero.resume' : 'hero.pause')" :title="t(heroPaused ? 'hero.resume' : 'hero.pause')" :aria-pressed="heroPaused" @click="heroPaused = !heroPaused"><Play v-if="heroPaused"/><Pause v-else/></button>
        </div>
        <a class="hero-cta" href="#buy">{{ t('nav.buy') }}<ArrowRight/></a>
      </div>
    </header>

    <section id="buy" class="panel purchase-panel">
      <div class="section-head">
        <div><h2>{{ t('buy.title') }}</h2></div>
        <span class="live-status" :class="{ ended: saleEnded }"><i></i>{{ saleEnded ? t('buy.ended') : t('buy.live') }}</span>
      </div>
      <div class="seat-grid">
        <button v-for="option in amountOptions" :key="option.amount" class="seat" :class="{ selected: selectedAmount === option.amount }" :aria-pressed="selectedAmount === option.amount" :disabled="saleEnded" @click="selectedAmount = option.amount">
          <i class="seat-check"><Check/></i><span class="seat-level">{{ option.name }}</span><strong>{{ option.amount }}</strong><small>BNB</small>
        </button>
      </div>
      <div class="order-summary">
        <div><span>{{ t('wallet.balance') }}</span><b>{{ walletBalance }} <small>BNB</small></b></div>
        <div><span>{{ t('buy.gas') }}</span><b>{{ gasEstimate }} <small>BNB</small></b></div>
      </div>
      <button v-if="referrer && isBound === false" class="referrer" type="button" @click="openInviteModal(false)"><span>{{ t('buy.referrer') }}</span><code>{{ shortAddress(referrer, 9) }}</code><i>{{ referrerConfirmed ? t('buy.confirmed') : t('buy.pending') }}</i></button>
      <button class="primary-button" :disabled="purchaseDisabled" @click="purchase"><span>{{ purchaseLabel }}</span><ArrowRight/></button>
      <p class="fine-print">{{ saleEnded ? t('buy.endedNote') : t('buy.note') }}</p>
    </section>

    <section id="dashboard" class="dashboard-section">
      <div class="section-head outside"><div><h2>{{ t('dash.title') }}</h2></div><span class="sync-label"><i></i>{{ t('dash.sync') }}</span></div>
      <div class="summary-board">
        <article class="summary-item featured"><span>{{ t('dash.performance') }}</span><strong>{{ formatBnb(member.performance) }}<small> BNB</small></strong></article>
        <article class="summary-item"><span>{{ t('dash.directPerf') }}</span><strong>{{ formatBnb(member.directPerformance) }}<small> BNB</small></strong></article>
        <article class="summary-item"><span>{{ t('team.title') }}</span><strong>{{ directTotal }}<small>{{ t('dash.people') }}</small></strong></article>
        <article class="summary-item featured"><span>{{ t('dash.shareTeamPerf') }}</span><strong>{{ teamPerformance === null ? '--' : formatBnb(teamPerformance) }}<small> BNB</small></strong><em>{{ t('dash.shareTeamNote') }}</em></article>
      </div>
      <p class="reward-note"><i><Check/></i> {{ t('dash.reward') }}</p>
    </section>

    <section class="panel invite-panel">
      <div class="section-head"><div><h2>{{ t('invite.title') }}</h2></div><span class="share-mark"><Share2/></span></div>
      <p>{{ t('invite.desc') }}</p>
      <div class="copy-field"><span>{{ inviteLink || t('invite.placeholder') }}</span><button :disabled="!inviteLink" @click="copyText(inviteLink, t('invite.link'))">{{ copyState }}</button></div>
    </section>

    <section id="share" class="panel share-panel">
      <div class="section-head"><div><h2>{{ t('share.title') }}</h2></div><span class="count-chip">{{ t('share.chip', { count: leaderboardTotal }) }}</span></div>
      <div v-if="!walletAddress" class="empty-state"><strong>{{ t('share.connectTitle') }}</strong><button @click="connectWallet">{{ t('nav.connect') }}</button></div>
      <DepositTable v-else ranked :rows="leaderboard" :total="leaderboardTotal" :page="leaderboardPage" :page-size="leaderboardPageSize" :loading="leaderboardLoading" :error="leaderboardError" :empty-text="t('share.emptyTitle')" :explorer-url="overview.explorerUrl" @page="loadLeaderboard" @copy="copyText($event, t('wallet.address'))"/>
    </section>

    <section id="team" class="panel team-panel">
      <div class="section-head"><div><h2>{{ t('team.title') }}</h2></div><span class="count-chip">{{ t('team.directCount', { count: directTotal }) }}</span></div>
      <div v-if="!walletAddress" class="empty-state"><strong>{{ t('team.connectTitle') }}</strong><button @click="connectWallet">{{ t('nav.connect') }}</button></div>
      <DepositTable v-else :rows="directMembers" :total="directTotal" :page="directPage" :page-size="directPageSize" :loading="teamLoading" :error="teamError" :empty-text="t('team.emptyTitle')" :explorer-url="overview.explorerUrl" @page="loadDirectMembers" @copy="copyText($event, t('wallet.address'))"/>
    </section>

    <section id="trust" class="panel trust-panel">
      <div class="section-head"><div><h2>{{ t('trust.title') }}</h2></div><div class="bnb-emblem">BNB</div></div>
      <div class="rule-box"><span>QUEUE LOGIC</span><strong>{{ t('trust.ruleTitle') }}</strong><p>{{ t('trust.ruleDesc') }}</p><div class="rule-flow"><i>AMOUNT</i><ArrowRight/><i>BLOCK TIME</i><ArrowRight/><i>QUEUE</i></div></div>
      <div class="trust-grid"><div><i class="trust-dot success"></i><span>{{ t('trust.network') }}</span><strong>BNB Chain</strong></div><a class="trust-link" :href="contractCodeUrl" target="_blank" rel="noopener"><i class="trust-dot success"></i><span>{{ t('trust.audit') }}</span><strong>{{ t('trust.viewCode') }}</strong></a><div><i class="trust-dot success"></i><span>{{ t('trust.fund') }}</span><strong>{{ t('trust.fundValue') }}</strong></div><div><i class="trust-dot success"></i><span>{{ t('trust.rule') }}</span><strong>{{ t('trust.ruleValue') }}</strong></div></div>
      <div class="contract-field"><div><span>IDO CONTRACT</span><code>{{ shortAddress(overview.contractAddress, 11) }}</code></div><button @click="copyText(overview.contractAddress, t('trust.contract'))"><Copy/>{{ t('trust.copy') }}</button><a :href="contractUrl" target="_blank" rel="noopener" :aria-label="t('trust.viewContract')"><ExternalLink/></a></div>
      <div class="faq-list"><article v-for="(item,index) in faqs" :key="item.q" :class="{ open: faqOpen === index }"><button @click="faqOpen = faqOpen === index ? -1 : index"><span>{{ item.q }}</span><Plus/></button><div><p>{{ item.a }}</p></div></article></div>
    </section>

    <footer><div class="footer-brand"><img class="brand-seal" src="/images/hs-logo-front-v4.png" alt="HS" width="27" height="27"><b>{{ t('brand.name') }}</b></div><p>HS · BNB CHAIN</p><small>{{ t('footer.risk') }}</small><div><span>CONTRACT VERIFIED</span><i></i><span>ON-CHAIN DATA</span></div></footer>

    <transition name="modal">
      <div v-if="inviteModalOpen" class="modal-backdrop" @click.self="closeInviteModal">
        <section class="invite-modal" role="dialog" aria-modal="true" aria-labelledby="invite-modal-title">
          <div class="modal-head"><span class="modal-icon"><Share2/></span><div><small>REFERRAL CONFIRMATION</small><h2 id="invite-modal-title">{{ t('modal.title') }}</h2></div><button :aria-label="t('modal.close')" @click="closeInviteModal"><X/></button></div>
          <p>{{ t('modal.desc') }}</p>
          <label for="referrer-address">{{ t('modal.label') }}</label>
          <div class="address-input" :class="{ invalid: inviteError }"><input id="referrer-address" v-model.trim="inviteInput" type="text" inputmode="text" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="0x..." @input="inviteError = ''"><button v-if="inviteInput" :aria-label="t('modal.clear')" @click="inviteInput = ''; inviteError = ''"><X/></button></div>
          <div class="input-status"><span v-if="inviteError" class="error-text">{{ inviteError }}</span><span v-else-if="referrer">{{ t('modal.fromLink') }}</span><span v-else>{{ t('modal.needAddress') }}</span></div>
          <div class="binding-warning"><i><Check/></i><span><strong>{{ t('modal.bindTitle') }}</strong><small>{{ t('modal.bindDesc') }}</small></span></div>
          <div class="modal-actions"><button class="secondary-button" :disabled="bindingPending" @click="closeInviteModal">{{ t('modal.cancel') }}</button><button class="confirm-button" :disabled="bindingPending" @click="confirmInvite"><span>{{ bindingPending ? t('modal.confirming') : t('modal.confirm') }}</span><ArrowRight/></button></div>
        </section>
      </div>
    </transition>
    <transition name="toast"><div v-if="toast" class="toast">{{ toast }}</div></transition>
  </main>
</template>

<script>
import { mapActions, mapState } from 'pinia'
import HeroLandscape from './components/HeroLandscape.vue'
import DepositTable from './components/DepositTable.vue'
import { useIdoStore } from './stores/ido'
import { localeState, setLocale, t as translate } from './i18n'
import { ArrowRight, Check, Copy, ExternalLink, Languages, LogOut, Menu, Pause, Play, Plus, Share2, Wallet, X } from 'lucide-vue-next'

const DK_RATE = Number(process.env.VUE_APP_DK_PER_BNB || 10000)
const SEAT_KEYS = [
  { amount: '0.3', key: 'seat.bronze', rate: 0.3 },
  { amount: '0.6', key: 'seat.silver', rate: 0.6 },
  { amount: '1', key: 'seat.gold', rate: 1 },
]

export default {
  name: 'App',
  components: { HeroLandscape, DepositTable, ArrowRight, Check, Copy, ExternalLink, Languages, LogOut, Menu, Pause, Play, Plus, Share2, Wallet, X },
  data() {
    return {
      saleEnded: false,
      heroPaused: false,
      selectedAmount: '0.6', menuOpen: false, walletMenuOpen: false, langMenuOpen: false, inviteModalOpen: false, inviteInput: '', inviteError: '', copyStateKey: 'copy.action', toast: '', faqOpen: -1, 
    }
  },
  watch: {
    inviteModalOpen(value) { document.body.style.overflow = value ? 'hidden' : '' },
    pendingWalletAddress(value) { if (value) this.openInviteModal() }
  },
  computed: {
    ...mapState(useIdoStore, ['teamPerformance']),
    ...mapState(useIdoStore, ['walletAddress', 'pendingWalletAddress', 'member', 'directMembers', 'teamLoading', 'txPending', 'purchaseStatus', 'bindingPending', 'referrer', 'referrerConfirmed', 'isBound', 'walletBalance', 'gasEstimate', 'overview', 'teamError', 'directTotal', 'directPage', 'directPageSize', 'leaderboard', 'leaderboardTotal', 'leaderboardPage', 'leaderboardPageSize', 'leaderboardLoading', 'leaderboardError']),
    locale() { return localeState.locale },
    amountOptions() {
      void this.locale
      return SEAT_KEYS.map(item => ({ amount: item.amount, name: translate(item.key), dk: DK_RATE * item.rate }))
    },
    faqs() {
      void this.locale
      return [
        { q: translate('faq.q1'), a: translate('faq.a1') },
        { q: translate('faq.q2'), a: translate('faq.a2') },
        { q: translate('faq.q3'), a: translate('faq.a3') },
        { q: translate('faq.q4'), a: translate('faq.a4') },
      ]
    },
    walletLabel() { return this.walletAddress ? this.shortAddress(this.walletAddress, 5) : this.t('nav.connect') },
    purchaseLabel() {
      if (this.saleEnded) return this.t('buy.endedCta')
      if (this.txPending) return this.t('buy.confirming')
      if (this.walletAddress && this.purchaseStatus === 'checking') return this.t('buy.checking')
      if (this.walletAddress && this.purchaseStatus === 'purchased') return this.t('buy.completed')
      if (this.walletAddress && this.purchaseStatus === 'error') return this.t('buy.retryCheck')
      if (this.walletAddress) return this.t('buy.cta', { amount: this.selectedAmount })
      return this.t('buy.connectCta')
    },
    purchaseDisabled() {
      return this.saleEnded
        || this.txPending
        || Boolean(this.walletAddress && (this.purchaseStatus === 'checking' || this.purchaseStatus === 'purchased'))
    },
    copyState() { void this.locale; return translate(this.copyStateKey) },
    selectedOption() { return this.amountOptions.find(item => item.amount === this.selectedAmount) || this.amountOptions[1] },
    inviteLink() { return this.walletAddress ? `${window.location.origin}${window.location.pathname}?ref=${this.walletAddress}` : '' },
    contractUrl() { const base = (this.overview.explorerUrl || 'https://bscscan.com').replace(/\/$/, ''); return this.overview.contractAddress ? `${base}/address/${this.overview.contractAddress}` : base },
    contractCodeUrl() { return this.overview.contractAddress ? `${this.contractUrl}#code` : this.contractUrl }
  },
  async mounted() {
    this.captureReferrer()
    this.loadPublicData()
    try {
      await this.restore()
    } catch (_) { /* wallet restore is optional */ }
  },
  beforeUnmount() { document.body.style.overflow = ''; window.clearTimeout(this.toastTimer) },
  methods: {
    ...mapActions(useIdoStore, ['connect', 'disconnect', 'restore', 'buy', 'bindReferral', 'cancelPendingConnection', 'setReferrer', 'loadDirectMembers', 'loadLeaderboard', 'captureReferrer', 'loadPublicData']),
    t: translate,
    switchLocale(locale) {
      setLocale(locale)
      this.langMenuOpen = false
      this.copyStateKey = 'copy.action'
    },
    handleWalletChip() {
      if (!this.walletAddress) return this.connectWallet()
      this.menuOpen = false
      this.langMenuOpen = false
      this.walletMenuOpen = !this.walletMenuOpen
    },
    async connectWallet() {
      try {
        const result = await this.connect()
        if (result && result.requiresBinding) this.openInviteModal()
        return Boolean(this.walletAddress)
      } catch (error) { this.showToast(error.message); return false }
    },
    async disconnectWallet() {
      await this.disconnect()
      this.walletMenuOpen = false
      this.inviteModalOpen = false
      this.showToast(this.t('wallet.disconnected'))
    },
    openInviteModal() {
      this.walletMenuOpen = false
      this.langMenuOpen = false
      this.inviteInput = this.referrer || ''
      this.inviteError = ''
      this.inviteModalOpen = true
    },
    closeInviteModal() {
      if (this.bindingPending) return
      this.inviteModalOpen = false
      this.inviteError = ''
      if (this.pendingWalletAddress) this.cancelPendingConnection()
    },
    async confirmInvite() {
      try {
        this.setReferrer(this.inviteInput)
        await this.bindReferral()
        this.inviteModalOpen = false
        this.showToast(this.t('modal.success'))
      } catch (error) { this.inviteError = error.message }
    },
    async purchase() {
      if (this.saleEnded) return this.showToast(this.t('error.saleEnded'))
      if (!this.walletAddress) {
        await this.connectWallet()
        if (!this.walletAddress) return
      }
      await this.executePurchase()
    },
    async executePurchase() { try { await this.buy(this.selectedAmount); this.showToast(this.t('buy.success')) } catch (error) { this.showToast(error.message) } },
    async copyText(value, label) {
      if (!value) return this.showToast(this.t('copy.missing', { label }))
      let copied = false
      try { if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(value); copied = true } } catch (_) { copied = false }
      if (!copied) {
        const input = document.createElement('textarea'); input.value = value; input.setAttribute('readonly', ''); input.style.cssText = 'position:fixed;opacity:0;left:-9999px'; document.body.appendChild(input); input.select(); input.setSelectionRange(0, input.value.length)
        try { copied = document.execCommand('copy') } catch (_) { copied = false } document.body.removeChild(input)
      }
      this.copyStateKey = copied ? 'copy.done' : 'copy.manual'
      this.showToast(copied ? this.t('copy.copied', { label }) : this.t('copy.fail'))
      window.setTimeout(() => { this.copyStateKey = 'copy.action' }, 1800)
    },
    shortAddress(address, size = 6) { return address ? `${address.slice(0, size)}…${address.slice(-4)}` : this.t('common.pending') },
    formatBnb(value) { const number = Number(value || 0); return number.toLocaleString('en-US', { minimumFractionDigits: number ? 1 : 2, maximumFractionDigits: 4 }) },
    formatInt(value) { return Number(value || 0).toLocaleString('en-US') },
    showToast(message) { this.toast = message || this.t('common.incomplete'); window.clearTimeout(this.toastTimer); this.toastTimer = window.setTimeout(() => { this.toast = '' }, 2600) }
  }
}
</script>

<style src="./style.css"></style>
